<script lang="ts">
const __DsBlock = {
	icon: 'lucide:text-cursor-input',
	name: '$t:blocks.input_number.name',
	description: '$t:blocks.input_number.description',
	type: 'input',
	category: 'input',
	fieldBinding: {
		supportedTypes: ({ primitiveType }) => {
			if (isPrimitiveTypeDecimal(primitiveType)) return [];

			const types = ['number'];
			if (isPrimitiveTypeInteger(primitiveType)) types.push('identifier');
			return types;
		},
	},
	options: [
		{
			name: 'modelValue',
			label: '$t:blocks.common.props.value',
			model: true,
		},
		{
			name: 'min',
			label: '$t:blocks.common.props.min',
		},
		{
			name: 'max',
			label: '$t:blocks.common.props.max',
		},
		{
			name: 'step',
			label: '$t:blocks.common.props.step',
		},
		{
			name: 'stepSnapping',
			label: '$t:blocks.common.props.step_snapping',
		},
		{
			name: 'placeholder',
			label: '$t:blocks.common.props.placeholder',
		},
		{
			name: 'variant',
			label: '$t:blocks.common.props.variant',
			type: 'enum',
			values: BLOCK_INPUT_VARIANT_OPTIONS,
		},
		{
			name: 'size',
			label: '$t:blocks.common.props.size',
			type: 'enum',
			values: BLOCK_CONTROL_SIZE_OPTIONS,
		},
		{
			name: 'block',
			label: '$t:blocks.common.props.block',
		},
		{
			name: 'disabled',
			label: '$t:blocks.common.props.disabled',
		},
		{
			name: 'readonly',
			label: '$t:blocks.common.props.readonly',
		},
		{
			name: 'disabledNotice',
			label: '$t:blocks.common.props.disabled_notice',
		},
	],
}
</script>
<script lang="ts" setup>
import type { InputBlockProps } from '#layers/blocks/types/input-block';
import type { InputNumberProps } from '#layers/ui/public';
import { reactivePick } from '@vueuse/core';
import { computed } from 'vue';
import { defineBlock } from '#layers/block-engine/public';
import { BLOCK_CONTROL_SIZE_OPTIONS, BLOCK_INPUT_VARIANT_OPTIONS } from '#layers/blocks/constants/ui-options';
import { getNumberInputOptions, isPrimitiveTypeDecimal, isPrimitiveTypeInteger } from '#layers/data-model/public';
import { usePrimitiveFormField } from '#layers/forms/public';
import { useDisabledNotice } from '#layers/ui/public';

interface Props extends
	InputBlockProps,
	Pick<InputNumberProps, 'placeholder' | 'variant' | 'size' | 'min' | 'max' | 'step' | 'stepSnapping' | 'formatOptions'> {
	/**
	 * Render the input full width
	 */
	block?: boolean;
}

// `stepSnapping` restates reka's opposite default: snapping a float to whole steps destroys the
// value, so the safe setting has to be declared rather than left to the absent-boolean cast.
const props = withDefaults(defineProps<Props>(), { block: true, stepSnapping: false });
const model = defineModel<number>();

;

const forwarded = reactivePick(props, 'placeholder', 'variant', 'size', 'min', 'max', 'step', 'readonly');
const { modelValue, disabled, disabledNotice, field } = usePrimitiveFormField(props, { model });
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });

// An integer column cannot hold a fraction, so the type forces snapping on and the block's own
// option can only ever add it. Precision is type-derived too; unbound, the block is free-form and
// takes the float options rather than the formatter's three-decimal default.
const numberOptions = computed(() => getNumberInputOptions(field.value?.type ?? 'float64'));
const formatOptions = computed(() => {
	if (props.formatOptions === undefined) return numberOptions.value?.formatOptions;

	return { ...numberOptions.value?.formatOptions, ...props.formatOptions };
});
const stepSnapping = computed(() => numberOptions.value?.stepSnapping || props.stepSnapping);
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<!-- `block` widens, it must not grow: the form's control column is a vertical flex box, so
			`flex-1` would stretch the root over a taller row and fling the absolute steppers apart. -->
		<DInputNumber
			v-bind="forwarded"
			v-model="modelValue"
			:format-options
			:step-snapping
			:disabled
			:aria-readonly="readonly"
			class="min-w-0"
			:class="{ 'w-full': block }"
		/>
	</DTooltip>
</template>
